import type { Promotion, PendingPromotionResponse, PromotionClaimResponse } from '@promotion/types'
import { Client as GameCommsClient, ENGAGE_CHANNEL, SCRATCHCARD_TOPIC, WHEEL_OF_FORTUNE_TOPIC, type PromotionClosedPayload } from '@spilnu/game-comms'
import { promotionCookie } from '@promotion/utils/promotionCookie'
import { ref, type Ref, navigateTo, useApi, usePromotionConfig, usePromotionLogger, useState, useGame, useRewardChecks, useApm, useCoreConfig, useUserInfo, useQuery, type MaybeRefOrGetter, toValue, isRefOrGetter, useMutation, computed, useQueryClient, ApiError, whenever, useSeamlessWalletAccountDetails, useI18n } from '#imports'
import type { UseQueryFunctionOptions } from '@core/types'

export interface UseRewardsOptions {
    isReminder?: boolean
    closeCallback?: any
}

export const useRewards = (options: UseRewardsOptions = {}) => {
    const { t } = useI18n()
    const { site } = useCoreConfig()
    const { translations } = usePromotionConfig()
    const { start } = useGame()
    const promoCookie = promotionCookie('promotionCookie')
    const logger = usePromotionLogger().withTag('useRewards')
    const api = useApi({
        enableLegacySupport: true,
        service: 'monolith'
    })
    const queryClient = useQueryClient()

    const {
        REQUIREMENT_TYPE_SPEND,
        STATUS_PENDING_REQUIREMENT,
        isBingoTickets,
        isBonusCash,
        isBonusSpins,
        isScratchCards,
        isWheelOfFortuneSpins,
    } = useRewardChecks()

    const apmAgent = useApm()

    const iframeUrl = useState<string | null>('promotion:iframeurl', () => null)
    const isPromotionLoading = useState<boolean>('promotion:iframe:loading', () => false)

    whenever(iframeUrl, () => {
        isPromotionLoading.value = true
    })

    const combinedGame = useState<PendingPromotionResponse['CombinedGame']>('promotion:claim:combined-game', () => undefined)

    const {
        isReminder = false,
        closeCallback = null,
    } = options

    function handlePromotionCookie(value: string) {
        if (!value) return

        if (promoCookie.value) {
            promoCookie.value.push(value)

            return
        }

        promoCookie.value = [value]
    }

    function useGetPendingReward(options: Omit<UseQueryFunctionOptions<any>, 'key'> = {}) {
        const { enabled, ...queryOptions } = options

        const { isLoggedIn, status: userInfoStatus } = useUserInfo()

        return useQuery(() => {
            return api('/promotions/get_pending_offer', {
                query: {
                    brand: site.brand
                }
            })
        }, {
            server: false,
            lazy: true,
            ...queryOptions,
            queryCache: {
                ttl: 2 // Reduce TTL to ensure same promotion won't be fetched twice
            },
            key: 'promotions:rewards:pending',
            enabled: () => {
                if (!isLoggedIn.value) return false
                if (userInfoStatus.value === 'pending') return false
                if (options.enabled === false) return false

                if (isRefOrGetter(options.enabled)) {
                    return toValue(options.enabled)
                }

                return options.enabled ?? true
            }
        })
    }

    function useGetReminder(options: Omit<UseQueryFunctionOptions<{
        reminders: Array<PendingPromotionResponse>
    }>, 'key'> & {
        id?: MaybeRefOrGetter<string | number | undefined>
    } = {}) {
        const { id, enabled = true, ...queryOptions } = options

        const { isLoggedIn, status: userInfoStatus } = useUserInfo()
        const { data: accountDetails } = useSeamlessWalletAccountDetails()

        const query = useQuery(() => {
            let endpoint = '/promotions/get_reminder'

            const idRaw = toValue(id)
            if (idRaw) {
                endpoint += `/${idRaw}`
            }

            return api<{
                reminders: Array<PendingPromotionResponse>
            }>(endpoint, {
                query: {
                    brand: site.brand
                }
            })
        }, {
            server: false,
            lazy: true,
            ...queryOptions,
            // Reminder is shared so key does not need to change, when id is provided
            key: 'promotions:rewards:reminder',
            enabled: () => {
                if (!isLoggedIn.value) return false
                if (userInfoStatus.value === 'pending') return false
                if (!toValue(enabled)) return false

                if (isRefOrGetter(id)) {
                    return !!toValue(id)
                }

                return true
            },
        })

        if (import.meta.client) {
            if (isRefOrGetter(id)) {
                whenever(id, () => {
                    query.refresh()
                })
            }
        }

        function close(reminder: PendingPromotionResponse) {
            // Use the title for non promotion messages
            const useTitle = !reminder?.Promotion

            if (useTitle && reminder.title) {
                handlePromotionCookie(reminder.title)

                query.data.value.reminders = query.data.value.reminders.filter(r => r.title !== reminder?.Promotion?.id)

                return
            }

            if (reminder?.Promotion?.id) {
                handlePromotionCookie(reminder?.Promotion?.id)

                query.data.value.reminders = query.data.value.reminders.filter(r => r.title !== reminder?.Promotion?.id)
            }
        }

        function hasDepositButton(promotion: PendingPromotionResponse) {
            if (!promotion?.Promotion) return false

            // Do not show deposit button if the promotion is not pending
            if (promotion.PromotionClaim?.status !== STATUS_PENDING_REQUIREMENT) return false

            if (promotion.Promotion.requirement_type === REQUIREMENT_TYPE_SPEND) {
                // Do not show deposit button if the user has balance
                if (Number(accountDetails.value?.balance) > 0) return false

                // Show the deposit button for all other promotions than bonus spins
                return !isBonusSpins(promotion.Promotion)
            }

            return true
        }

        function hasAction(promotion: PendingPromotionResponse): boolean {
            if (promotion.CombinedGame) {
                if (promotion.PromotionClaim?.received_reward || promotion.Promotion.requirement_type === REQUIREMENT_TYPE_SPEND) return true
            }

            if (promotion.PromotionClaim?.received_reward && (promotion.ScratchCardConfiguration || promotion.WheelOfFortuneConfiguration)) return true

            return false
        }

        const data = computed(() => {
            if (!query.data.value) return query.data.value

            const reminders = [
                ...(query.data.value.reminders || [])
            ]
                .sort((a, b) => {
                    const aHasAction = hasAction(a)
                    const bHasAction = hasAction(b)

                    if (aHasAction === bHasAction) return 0

                    return aHasAction ? -1 : 1
                }).sort((a, b) => {
                    const aHasDepositButton = hasDepositButton(a)
                    const bHasDepositButton = hasDepositButton(b)

                    if (aHasDepositButton === bHasDepositButton) return 0

                    return aHasDepositButton ? -1 : 1
                }).slice(0, 3)

            return {
                ...query.data.value,
                reminders
            }
        })

        return query.enrich({
            data,
            close
        })
    }

    const gameHubReminderIds = new Map<string, Ref<string | undefined>>()
    for (const rewardType of ['scratchcard', 'wheel_of_fortune'] as const) {
        const reminderId = ref<string>()
        gameHubReminderIds.set(rewardType, reminderId)
        useGetReminder({ id: reminderId })
    }

    function getRewardImgPath(reward: Promotion, activeState = false) {
        if (reward.reward_image_path) {
            return reward.reward_image_path
        }

        let imgPath = '/theme/Spilnu/img/reward/'

        switch (true) {
            case isBingoTickets(reward):
                imgPath += 'bingo-tickets.png'
                break
            case isBonusSpins(reward):
                imgPath += 'bonus-spins.png'
                break
            case isScratchCards(reward):
                imgPath += 'scratchcards.png'
                break
            case isWheelOfFortuneSpins(reward):
                imgPath += 'wheel-of-fortune.png'
                break
            case isBonusCash(reward):
                imgPath += 'bonus-cash.png'
                break
            default:
                if (activeState) {
                    imgPath += 'present_open.png'
                }
                else {
                    imgPath += 'present.png'
                }
        }

        return imgPath
    }

    /**
     * sets up gamehub client to enable running entry_url in iframe
     * @param rewardType - The type of engage game
     */
    function setupGameHubSubscriber(rewardType: 'scratchcard' | 'wheel_of_fortune') {
        const reminderId = gameHubReminderIds.get(rewardType)!

        try {
            const closeEvent = rewardType === 'scratchcard' ? SCRATCHCARD_TOPIC.closed : WHEEL_OF_FORTUNE_TOPIC.closed
            const readyEvent = rewardType === 'scratchcard' ? SCRATCHCARD_TOPIC.ready : WHEEL_OF_FORTUNE_TOPIC.ready
            const channel = rewardType === 'scratchcard' ? ENGAGE_CHANNEL.scratchcard : ENGAGE_CHANNEL.wheelOfFortune

            const gameHubClient = new GameCommsClient({
                channel: 'page',
                group: 'promotions',
            })

            gameHubClient.subscribeTo(channel, readyEvent, () => {
                isPromotionLoading.value = false
            })

            gameHubClient.subscribeTo(channel, closeEvent, async (payload: PromotionClosedPayload) => {
                iframeUrl.value = null

                if (payload.slug || payload.game_id) {
                    // Saved game_id as fallback since scratchcard still sends this as slug -_-
                    await start({ gameSlug: payload.slug || payload.game_id })
                }

                if (isReminder) {
                    reminderId.value = payload.externalId

                    return
                }

                queryClient.invalidateQueries({
                    key: 'promotions:rewards'
                })
            })
        }
        catch (error) {
            logger.error('setupGameHubSubscriber failed with error: ', JSON.stringify(error))

            apmAgent.captureError(error)
        }
    }

    function useClaimPromotion(promotionOrPendingPromotion: MaybeRefOrGetter<Promotion | PendingPromotionResponse | undefined>) {
        const { isPromotion, isPendingPromotion } = useRewardChecks()

        const mutation = useMutation(async () => {
            const promotionOrPendingPromotionRaw = toValue(promotionOrPendingPromotion)

            if (!promotionOrPendingPromotionRaw) return

            const promotion = isPromotion(promotionOrPendingPromotionRaw) ? promotionOrPendingPromotionRaw : promotionOrPendingPromotionRaw.Promotion
            const pendingPromotion = isPendingPromotion(promotionOrPendingPromotionRaw) ? promotionOrPendingPromotionRaw : undefined

            if (isScratchCards(promotion)) {
                setupGameHubSubscriber('scratchcard')
            }

            if (isWheelOfFortuneSpins(promotion)) {
                setupGameHubSubscriber('wheel_of_fortune')
            }

            const externalId = promotion ? promotion.external_id : pendingPromotion?.Promotion?.external_id
            const promotionClaimId = promotion.promotionClaimId || pendingPromotion?.PromotionClaim?.id

            const response = await api<PromotionClaimResponse>('/promotions/claim', {
                method: 'post',
                body: {
                    externalId: externalId,
                    promotionClaimId: promotionClaimId ?? null,
                    pendingPromotionOfferId: pendingPromotion?.PendingPromotionOffer?.id ?? null,
                    brand: site.brand
                },
            })

            return {
                ...response,
                _context: {
                    promotion,
                    pendingPromotion
                }
            } as PromotionClaimResponse & {
                _context: {
                    promotion: typeof promotion
                    pendingPromotion: typeof pendingPromotion
                }
            }
        }, {
            async onSuccess(data) {
                if (!data) throw new Error('No data present')

                if (data.redirectUrl) {
                    await navigateTo(data.redirectUrl, { external: true })
                    return
                }

                if (data?.hasPaymentRequirement && !data?.willBeEnqueued) {
                    await navigateTo(t('pages.deposit'))
                    return
                }

                const { _context: { promotion, pendingPromotion } } = data

                // User claimed a promotion containing a scratch card, return and open scratch card
                if (isScratchCards(promotion)) {
                    if (typeof closeCallback === 'function') {
                        closeCallback(() => iframeUrl.value = data.scratch_card?.entry_url ?? '')

                        return
                    }

                    iframeUrl.value = data.scratch_card?.entry_url ?? ''
                }

                // User claimed a promotion containing wheel of fortune return and open wheel of fortune
                if (isWheelOfFortuneSpins(promotion)) {
                    if (typeof closeCallback === 'function') {
                        closeCallback(() => iframeUrl.value = data.spins_bundle?.entry_url ?? '')

                        return
                    }

                    iframeUrl.value = data.spins_bundle?.entry_url ?? ''
                }

                if (data.CombinedGame) {
                    const bingoTicketConfig = pendingPromotion?.BingoFreeTicketConfiguration
                    const bingoTickets = bingoTicketConfig !== undefined ? bingoTicketConfig.num_tickets : 0

                    const startOptions = {
                        gameSlug: Array.isArray(data.CombinedGame) ? data.CombinedGame[0]!.slug : data.CombinedGame.slug,
                        ticketsReceived: Number(bingoTickets),
                    }

                    // Start game with bonusspins once modal has been closed
                    if (typeof closeCallback === 'function') {
                        closeCallback(start, startOptions)

                        return
                    }

                    await start(startOptions)
                }

                if (isReminder) {
                    await queryClient.invalidateQueries({ key: 'promotions:rewards:reminder' })

                    return
                }

                if (data.receivedReward && typeof closeCallback === 'function') {
                    closeCallback('next', { claimed: true })

                    return
                }

                logger.warn('claimPromotion: closeCallback not set and/or reward\'s data.receivedReward is false')
            },
            onSettled() {
                if (!closeCallback) {
                    queryClient.invalidateQueries({
                        key: 'promotions:rewards'
                    })
                }
            }
        })

        const buttonText = computed(() => {
            const promotionOrPendingPromotionRaw = toValue(promotionOrPendingPromotion)

            if (!promotionOrPendingPromotionRaw) return translations.yesPlease

            const promotion = isPromotion(promotionOrPendingPromotionRaw) ? promotionOrPendingPromotionRaw : promotionOrPendingPromotionRaw.Promotion
            const pendingPromotion = isPendingPromotion(promotionOrPendingPromotionRaw) ? promotionOrPendingPromotionRaw : undefined

            if (mutation.pending.value) {
                return `${translations.loading}...`
            }

            if (pendingPromotion?.ctaText) {
                return pendingPromotion.ctaText
            }

            if (pendingPromotion?.hasPaymentRequirement) {
                return translations.yesPlease
            }

            if (isScratchCards(promotion)) {
                return translations.scratchNow
            }

            if (isWheelOfFortuneSpins(promotion)) {
                return translations.spinNow
            }

            if (isBonusSpins(promotion)) {
                return translations.playNow
            }

            if (isBingoTickets(promotion)) {
            // In the future we want to be able to say: "play {{bingo-name}} now" instead of just bingo.
                return translations.playBingoNow
            }

            return translations.yesPlease
        })

        const messages = computed(() => {
            const _messages: {
                save?: string
                error?: string
                game?: string
            } = {}

            if (mutation.data.value?.message) {
                if (!mutation.data.value.receivedReward) {
                    _messages.error = mutation.data.value.message
                }

                _messages.save = mutation.data.value.message

                if (isReminder) {
                    if (mutation.data.value.CombinedGame) {
                        combinedGame.value = Array.isArray(mutation.data.value.CombinedGame) ? mutation.data.value.CombinedGame[0] : mutation.data.value.CombinedGame
                    }

                    _messages.game = mutation.data.value?.message
                }
            }

            if (mutation.error.value) {
                const error = mutation.error.value

                if (ApiError.isError(error)) {
                    _messages.error = error.data?.message || error.message
                }
                else {
                    _messages.error = error.message
                }
            }

            return _messages
        })

        return {
            ...mutation,
            buttonText,
            messages
        }
    }

    function useDeclinePromotion(promotion: MaybeRefOrGetter<PendingPromotionResponse | undefined>) {
        const mutation = useMutation(async () => {
            const promotionRaw = toValue(promotion)

            if (!promotionRaw) return

            const { Promotion: { external_id }, PromotionClaim, PendingPromotionOffer } = promotionRaw

            const response = await api('/promotions/decline', {
                method: 'post',
                body: {
                    externalId: external_id,
                    promotionClaimId: PromotionClaim?.id ?? null,
                    pendingPromotionOfferId: PendingPromotionOffer?.id ?? null,
                    brand: site.brand
                },
            })

            return response
        })

        const messages = computed(() => {
            const _messages: {
                save?: string
                error?: string
            } = {}

            if (mutation.isSuccess.value) {
                _messages.save = translations.saveMessage
            }

            if (mutation.error.value) {
                const error = mutation.error.value

                if (ApiError.isError(error)) {
                    _messages.error = error?.data?.message || error.message
                }
                else {
                    _messages.error = error.message
                }
            }

            return _messages
        })

        return {
            ...mutation,
            messages
        }
    }

    return {
        iframeUrl,
        isPromotionLoading,

        getRewardImgPath,
        setupGameHubSubscriber,
        handlePromotionCookie,

        useGetPendingReward,
        useGetReminder,
        useClaimPromotion,
        useDeclinePromotion
    }
}
